/**
 * This is the entry point for Feature Hub App integration
 */
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { isTrackingDisabled } from '@oneaudi/audi-tracking-service';
import React from 'react';
import { getDesignLibrary } from '../alpha-helper';
import App from './FeatureApp';
import type {
  ContentService,
  Logger,
  TrackingService,
  OneGraphService,
  GfaLocaleService,
  ExtendedGfaLocaleServiceV1,
} from './content/feature-service-types';
import { FalconContextProvider } from './ui/context/cms-context/FalconContext';

interface RequiredFeatureServiceDependencies extends FeatureServices {
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 'onegraph-service': OneGraphService;
  readonly 'audi-content-service': ContentService;
}

interface OptionalFeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 'gfa:locale-service': ExtendedGfaLocaleServiceV1;
}

export type FeatureServiceDependencies = Required<RequiredFeatureServiceDependencies> &
  Partial<OptionalFeatureServiceDependencies>;

const requiredFeatureServices: { [service in keyof RequiredFeatureServiceDependencies]: string } = {
  'gfa:locale-service': '^1.0.0',
  'onegraph-service': '^1.0.0',
  'audi-content-service': '^1.0.0',
};

const optionalFeatureServices: { [service in keyof OptionalFeatureServiceDependencies]-?: string } =
  {
    's2:logger': '^1.0.0',
    'audi-tracking-service': '^2.0.0',
    'gfa:locale-service': '^1.0.0',
  };

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: requiredFeatureServices,
    externals: {
      react: '*',
      'styled-components': '*',
      '@oneaudi/onegraph-client': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: optionalFeatureServices,
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const logger = featureServices['s2:logger'] as Logger;
    const oneGraphClient = featureServices['onegraph-service'].getApolloClient();
    const contentService = featureServices['audi-content-service'];
    const localeService = featureServices['gfa:locale-service'];

    const loggerService = logger || console;
    loggerService.info('Feature App created.');

    const trackingService = !isTrackingDisabled()
      ? featureServices['audi-tracking-service']
      : undefined;
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    return {
      render: () => {
        return (
          <FalconContextProvider
            contentService={contentService}
            logger={loggerService}
            trackingService={trackingService}
            localeService={localeService}
          >
            <App oneGraphClient={oneGraphClient} trackingService={trackingService} />
          </FalconContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
